import { useEffect, useRef, useState } from "react";

import { AlertTriangle, XCircle } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTaskStatus } from "@/hooks/queries/use-bundle-splitter";
import { useIsSessionActive } from "@/hooks/useIsSessionActive";

import { TASK_ENDED_STATES } from "@constants/bundleSplitter";

import { FilePreview } from "../FilePreview";
import type { BundleRowStatus } from "../store/useBundleUploads";
import { STUCK_THRESHOLD_MS, computePollInterval } from "./pollingSchedule";

// MDRS-550: a status error flips the row out of busy (and the toast's X back
// to close), so ride out a transient 5xx — 4 attempts with backoff (~7 s)
// before a poll counts as failed, instead of the client-wide single retry.
const STATUS_POLL_RETRIES = 3;

type PollingPhaseProps = {
  file: File;
  taskId: string;
  onProcessingComplete: () => void;
  onDismiss?: () => void;
  onBusyChange?: (busy: boolean) => void;
  onStatusChange?: (status: BundleRowStatus) => void;
};

export const PollingPhase = ({
  file,
  taskId,
  onProcessingComplete,
  onDismiss,
  onBusyChange,
  onStatusChange,
}: PollingPhaseProps) => {
  const [processingStep, setProcessingStep] = useState<string>("Processing");
  const [processingProgress, setProcessingProgress] = useState(0);
  const [isTerminal, setIsTerminal] = useState(false);
  const [isStuck, setIsStuck] = useState(false);
  // useState lazy-initializer keeps render pure while pinning the mount time.
  const [mountedAt] = useState(() => Date.now());
  const lastProgressValueRef = useRef<number | null>(null);
  const lastProgressAtRef = useRef<number | null>(null);

  // MDRS-267: the bundle toast lives outside ProtectedRoute, so its polling
  // would otherwise keep firing (and painting live progress) after the session
  // expires. Gate both the query and its interval on session validity so it
  // stops the instant the user is no longer authenticated.
  const isSessionActive = useIsSessionActive();

  const {
    data: taskStatus,
    isSuccess: isStatusSuccess,
    isError: isStatusError,
  } = useTaskStatus({
    task_id: taskId,
    enabled: !!taskId && isSessionActive,
    // Stepped backoff: cadence lengthens as the task runs longer, keeping the
    // first 30 s responsive while preventing long-session poll storms. An
    // expired session halts polling entirely.
    refetchInterval:
      isTerminal || !isSessionActive
        ? false
        : () => computePollInterval(Date.now() - mountedAt),
    retry: STATUS_POLL_RETRIES,
  });

  // MDRS-550: busy until the task settles (success, failure or stuck). A
  // status call that keeps failing, or a task that reports no progress within
  // the stuck threshold, never reaches either, so those stop counting as busy
  // rather than hold the toast open; the row itself is unchanged and keeps
  // polling.
  //
  // This used to be the only thing standing between a closed tab and lost
  // documents, since the browser was the only thing that ever saved a split.
  // MDRS-548 (AP-12) moved that to the server; the busy flag is now about
  // keeping the user's own progress on screen, not about data safety.
  const reportsProgress = taskStatus?.progress?.progress != null;
  const [isSilentTooLong, setIsSilentTooLong] = useState(false);
  useEffect(() => {
    if (reportsProgress) return;
    const timer = setTimeout(() => setIsSilentTooLong(true), STUCK_THRESHOLD_MS);
    return () => clearTimeout(timer);
  }, [reportsProgress]);

  const isBusy =
    !isTerminal && !isStatusError && (reportsProgress || !isSilentTooLong);
  useEffect(() => {
    onBusyChange?.(isBusy);
  }, [isBusy, onBusyChange]);

  const isFailed =
    taskStatus?.state === "FAILURE" || taskStatus?.state === "REVOKED";

  // MDRS-550: the pop-up's per-stage counts. A task that's slow to report,
  // or whose status call keeps failing, still reads as splitting, as the row
  // shows it; one stuck past the threshold, or failed, counts as failed.
  const status: BundleRowStatus = isFailed || isStuck ? "failed" : "splitting";
  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);
  const isRetrying = taskStatus?.state === "RETRY";

  useEffect(() => {
    if (!isStatusSuccess || !taskStatus) return;

    if (taskStatus.progress?.step) {
      setProcessingStep(taskStatus.progress.step.replace(/\s+with\s+\w+/gi, ""));
    }

    if (taskStatus.progress?.progress != null) {
      const current = taskStatus.progress.progress;
      setProcessingProgress(current);
      // Alive again: a later silence gets its own full threshold.
      setIsSilentTooLong(false);

      // Watchdog: time-based so it's robust to changing poll cadence. We
      // record the timestamp of the last advancement and trip if no progress
      // has been observed for STUCK_THRESHOLD_MS. RETRY responses report
      // progress=null and are skipped (transient, not a real stall).
      if (
        lastProgressValueRef.current === null ||
        current > lastProgressValueRef.current
      ) {
        lastProgressValueRef.current = current;
        lastProgressAtRef.current = Date.now();
      } else if (
        current === lastProgressValueRef.current &&
        lastProgressAtRef.current !== null &&
        Date.now() - lastProgressAtRef.current >= STUCK_THRESHOLD_MS
      ) {
        setIsStuck(true);
        setIsTerminal(true);
      }
    }

    if (TASK_ENDED_STATES.has(taskStatus.state)) {
      setIsTerminal(true);
    }

    if (taskStatus.state === "SUCCESS") {
      onProcessingComplete();
    }
  }, [taskStatus, isStatusSuccess, onProcessingComplete]);

  return (
    <>
      <FilePreview
        file={file}
        uploadProgress={isFailed || isStuck ? undefined : processingProgress}
        isFailed={isFailed}
        onClear={isFailed || isStuck ? onDismiss : undefined}
      />

      {isFailed ? (
        <Alert variant="destructive" className="mt-2">
          <XCircle className="size-4" />
          <AlertDescription>
            Processing failed. Please dismiss this file and try again.
          </AlertDescription>
        </Alert>
      ) : isStuck ? (
        <Alert className="mt-2">
          <AlertTriangle className="size-4" />
          <AlertDescription>
            Taking longer than expected. Please dismiss and try again.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <p className="text-center text-xs text-foreground-muted">
            {isRetrying ? "Retrying…" : processingStep}
          </p>
          <p className="mt-2 text-center text-xs text-foreground-muted">
            This may take a few minutes depending on number and size of documents.
          </p>
        </>
      )}
    </>
  );
};
